/**
 * Warms up the microphone while a call is still ringing so the accept path is
 * faster. The bulk of accept latency is the RealtimeKit join, and part of that
 * is `getUserMedia` — which on the first call also shows the permission prompt.
 * Priming here grants permission and wakes the audio device ahead of time, then
 * immediately releases the track so the mic isn't held open during ringing.
 * Best-effort: a denial or missing device is swallowed — the real join surfaces
 * any genuine error.
 */
export const primeMicrophone = async () => {
  if (!navigator?.mediaDevices?.getUserMedia) return;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
  } catch {
    // noop — see docblock
  }
};

export const MIC_RESULTS = {
  OK: 'ok',
  UNSUPPORTED: 'unsupported',
  DENIED: 'denied',
  NO_DEVICE: 'no-device',
  FAILED: 'failed',
};

/**
 * Asks for the microphone and reports what happened, for the surfaces that
 * have to explain a refusal rather than swallow it. The public call page is
 * one: a customer who tapped the link inside Messenger or Instagram lands in
 * that app's in-app browser, where microphone access is unreliable, and needs
 * to be told to open the link in a real browser — not handed a raw SDK error.
 */
export const requestMicrophone = async () => {
  if (!navigator?.mediaDevices?.getUserMedia) return MIC_RESULTS.UNSUPPORTED;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    return MIC_RESULTS.OK;
  } catch (error) {
    if (['NotAllowedError', 'SecurityError'].includes(error?.name)) {
      return MIC_RESULTS.DENIED;
    }
    if (['NotFoundError', 'OverconstrainedError'].includes(error?.name)) {
      return MIC_RESULTS.NO_DEVICE;
    }
    return MIC_RESULTS.FAILED;
  }
};

/**
 * True in the in-app browsers that links open in from a chat app. Their
 * WebRTC support is the unreliable part, so the page warns before the tap
 * instead of after a failed join.
 */
export const isInAppBrowser = () =>
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|WhatsApp/i.test(
    navigator?.userAgent || ''
  );

/**
 * Plays remote participants' audio through hidden <audio> elements.
 *
 * The Cloudflare RealtimeKit core SDK delivers each remote participant's
 * `audioTrack` (a MediaStreamTrack) but never renders it — a headless
 * integration has to attach the track to an audio element itself, or no one
 * hears anyone. This wires that up for every participant already in the room
 * plus any who join later, and reacts to their `audioUpdate` events.
 *
 * Returns a detach function that removes all audio elements.
 */
export const createRemoteAudioPlayer = meeting => {
  const elements = new Map();

  const play = (participantId, track) => {
    if (!track) return;
    let el = elements.get(participantId);
    if (!el) {
      el = document.createElement('audio');
      el.autoplay = true;
      el.setAttribute('playsinline', '');
      el.style.display = 'none';
      document.body.appendChild(el);
      elements.set(participantId, el);
    }
    el.srcObject = new MediaStream([track]);
    // Autoplay is permitted because joining a call is a user gesture; the
    // catch guards the rare blocked case so it never throws into the caller.
    el.play?.().catch(() => {});
  };

  const stop = participantId => {
    const el = elements.get(participantId);
    if (!el) return;
    el.srcObject = null;
    el.remove();
    elements.delete(participantId);
  };

  const bindParticipant = participant => {
    if (participant.audioEnabled && participant.audioTrack) {
      play(participant.id, participant.audioTrack);
    }
    participant.on('audioUpdate', ({ audioEnabled, audioTrack }) => {
      if (audioEnabled && audioTrack) play(participant.id, audioTrack);
      else stop(participant.id);
    });
  };

  // The other party is often already in the room before we finish joining, so
  // participantJoined never fires for them — bind the current members too.
  meeting.participants.joined.toArray().forEach(bindParticipant);
  meeting.participants.joined.on('participantJoined', bindParticipant);
  meeting.participants.joined.on('participantLeft', participant =>
    stop(participant.id)
  );

  return () => {
    elements.forEach((_el, participantId) => stop(participantId));
  };
};
